import { Fragment, useEffect, useState } from "react";
import {
  Box, Stack, Typography, Chip, Alert, CircularProgress, IconButton, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Collapse,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddCommentIcon from "@mui/icons-material/AddComment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Link as RouterLink } from "react-router-dom";
import { vigiaPrompt } from "../lib/vigiaPrompt";
import {
  getPaisesRange, PaisesRange, Paises, RespuestaHttp, countryName, countryFlag, fmtArDateTime,
  getBitacora, investigarPais, addComentario, geoToggle, geoAllowToggle, BitacoraEntry,
  vigiaAccion, VigiaInvestigacion,
} from "../services/monitoring";

const RANGOS: { key: PaisesRange; label: string }[] = [
  { key: "day",   label: "Último día" },
  { key: "week",  label: "Última semana" },
  { key: "month", label: "Último mes" },
  { key: "all",   label: "Histórico" },
];

const TIPO_META: Record<string, [string, string, string]> = {
  investigacion: ["#1E40AF", "#DBEAFE", "Investigación"],
  comentario:    ["#166534", "#DCFCE7", "Comentario"],
  geo:           ["#9A3412", "#FFEDD5", "Geo"],
  alerta:        ["#B91C1C", "#FEE2E2", "Alerta"],
  vigia:         ["#5B21B6", "#EDE9FE", "Vigía"],
};

const NIVEL_META: Record<string, [string, string, string]> = {
  actuar:  ["#B91C1C", "#FEE2E2", "ACTUAR"],
  vigilar: ["#B45309", "#FEF3C7", "Vigilar"],
  ruido:   ["#374151", "#E5E7EB", "Ruido"],
};
// Acción recomendada por Claude (fase 3) → etiqueta y color del chip.
const ACCION_META: Record<string, [string, string, string]> = {
  bloquear:        ["#B91C1C", "#FEE2E2", "Bloquear"],
  vigilar:         ["#B45309", "#FEF3C7", "Vigilar"],
  suprimir:        ["#1E40AF", "#DBEAFE", "Suprimir"],
  vendor_conocido: ["#065F46", "#D1FAE5", "Vendor conocido"],
  ignorar:         ["#374151", "#E5E7EB", "Ignorar"],
};
type AccionOperador = "suprimir" | "vendor_conocido" | "ignorar";
const ACCION_LABEL: Record<AccionOperador, string> = {
  suprimir: "Suprimir 7 días", vendor_conocido: "Marcar vendor conocido", ignorar: "Ignorar",
};

const nf = new Intl.NumberFormat("es-AR");

/** Color por clase de código HTTP: 2xx verde, 3xx gris, 4xx ámbar, 5xx rojo. */
function statusColor(status: number): [string, string] {
  if (status >= 500) return ["#B91C1C", "#FEE2E2"];
  if (status >= 400) return ["#B45309", "#FEF3C7"];
  if (status >= 300) return ["#374151", "#E5E7EB"];
  return ["#166534", "#DCFCE7"];
}

/** "Nuestra respuesta" de una fila de investigación: chips por código HTTP. */
function Respuestas({ rows }: { rows?: RespuestaHttp[] }) {
  if (rows === undefined) return <span style={{ color: "#9CA3AF" }}>—</span>;
  if (rows.length === 0) return <span style={{ color: "#9CA3AF" }} title="Sin match en los access logs del ALB">sin datos</span>;
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {rows.slice(0, 4).map((r, i) => {
        const [fg, bg] = statusColor(r.status);
        return (
          <Chip key={i} size="small"
            label={`${r.status}${r.borde ? " borde" : ""} ×${nf.format(r.n)}`}
            title={r.borde ? "Respondido por el WAF/ALB: no llegó a la aplicación" : "Respondido por la aplicación"}
            sx={{ height: 20, fontSize: 11, fontWeight: 700, color: fg, bgcolor: bg, fontFamily: "monospace" }} />
        );
      })}
      {rows.length > 4 && <span style={{ fontSize: 11, color: "#6B7280" }}>+{rows.length - 4}</span>}
    </Box>
  );
}

/** Últimos hits del vigía (Lambda `vigia`, cada 10 min): detector, quién, veredicto. */
function InvestigacionIp({ inv }: { inv: VigiaInvestigacion }) {
  const vend = inv.vendor?.por_usuario ?? {};
  const iids = Array.from(new Set(Object.values(vend).filter((v): v is number => v != null)));
  const cols = inv.vendor?.collectors ?? {};
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
        {inv.ip}{inv.ptr ? <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}> · {inv.ptr}</Typography> : null}
      </Typography>
      <Typography variant="body2">{inv.resumen ?? "sin datos"}</Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, fontSize: 12 }}>
        {iids.length > 0 && (
          <Box><b>Vendor:</b> {iids.map((i) => `${i}${cols[String(i)]?.length ? ` (${cols[String(i)].slice(0, 3).join(", ")})` : ""}`).join(", ")}</Box>
        )}
        {inv.usuarios && inv.usuarios.usuarios.length > 0 && (
          <Box><b>Usuarios 24 h:</b> {inv.usuarios.usuarios.slice(0, 6).map((u) => u.usuario).join(", ")}{inv.usuarios.usuarios.length > 6 ? " …" : ""}</Box>
        )}
        {inv.waf && (
          <Box><b>WAF 24 h:</b> {inv.waf.requests} req{inv.waf.bloqueadas ? `, ${inv.waf.bloqueadas} bloqueadas` : ""}
            {inv.waf.headers?.["user-agent"] ? ` · UA ${inv.waf.headers["user-agent"].slice(0, 60)}` : ""}</Box>
        )}
        {inv.alb && (
          <Box><b>ALB {inv.alb.dias} d:</b> {inv.alb.dias_con_trafico} días con tráfico{inv.alb.primer_dia ? ` desde ${inv.alb.primer_dia}` : ""}
            {" · "}{Object.entries(inv.alb.codigos).map(([k, v]) => `${k} ${v}`).join(", ")}
            {Object.keys(inv.alb.login_por_codigo ?? {}).length ? ` · login ${Object.entries(inv.alb.login_por_codigo).map(([k, v]) => `${k}×${v}`).join(" ")}` : ""}
            {inv.alb.admin ? <b style={{ color: "#B91C1C" }}> · {inv.alb.admin} a /admin/*</b> : ""}</Box>
        )}
        {inv.pares?.ips_con_volumen_parecido != null && (
          <Box><b>IPs con volumen parecido:</b> {inv.pares.ips_con_volumen_parecido}{inv.pares.ips_nuevas_en_historia ? ` (${inv.pares.ips_nuevas_en_historia} nuevas en la historia)` : ""}</Box>
        )}
      </Stack>
      {inv.waf && inv.waf.top.length > 0 && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5, fontFamily: "monospace" }}>
          {inv.waf.top.slice(0, 6).map((t) => `${t.metodo} ${t.uri} ${t.n}${t.accion !== "ALLOW" ? ` (${t.accion})` : ""}`).join(" · ")}
        </Typography>
      )}
      {inv.errores && inv.errores.length > 0 && (
        <Typography variant="caption" sx={{ color: "#B45309", display: "block" }}>Sin datos de: {inv.errores.join("; ")}</Typography>
      )}
    </Box>
  );
}

type AccionPendiente = { entry: BitacoraEntry; accion: AccionOperador; ip?: string; clave?: string; iid?: number | null; nota: string };

function VigiaTable({ rows, onCambio }: { rows: BitacoraEntry[]; onCambio: () => Promise<void> | void }) {
  // Clave de la fila cuyo prompt se acaba de copiar (tooltip "¡Copiado!" 2 s).
  const [copiada, setCopiada] = useState<string | null>(null);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [pendiente, setPendiente] = useState<AccionPendiente | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function copiarPrompt(e: BitacoraEntry, key: string) {
    try { await navigator.clipboard.writeText(vigiaPrompt(e)); setCopiada(key); setTimeout(() => setCopiada(null), 2000); }
    catch { /* clipboard no disponible */ }
  }
  function pedir(entry: BitacoraEntry, accion: AccionOperador) {
    const hits = entry.resultado?.hits ?? [];
    const ip = hits.find((h) => h.ip)?.ip ?? undefined;
    const inv = ip ? entry.resultado?.investigacion?.[ip] : undefined;
    const iids = Object.values(inv?.vendor?.por_usuario ?? {}).filter((v): v is number => v != null);
    const nota = inv?.resumen ? inv.resumen.slice(0, 120) : "";
    setPendiente({ entry, accion, ip, clave: hits[0]?.clave, iid: iids[0] ?? null, nota });
  }
  async function confirmar() {
    if (!pendiente?.entry.sk) return;
    setBusy(true); setErr(null);
    try {
      await vigiaAccion({ sk: pendiente.entry.sk, accion: pendiente.accion, clave: pendiente.clave, ip: pendiente.ip,
                          iid: pendiente.iid, horas: 24 * 7, nota: pendiente.nota || undefined });
      setPendiente(null);
      await onCambio();
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell sx={{ whiteSpace: "nowrap" }}>Fecha</TableCell>
            <TableCell>Nivel</TableCell>
            <TableCell>Detectores</TableCell>
            <TableCell>Quién</TableCell>
            <TableCell>Resumen</TableCell>
            <TableCell>Recomienda</TableCell>
            <TableCell padding="checkbox" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 2, color: "text.secondary" }}>
              Sin hits del vigía todavía.
            </TableCell></TableRow>
          )}
          {rows.map((e) => {
            const r = e.resultado ?? null;
            const nivel = r?.nivel ?? "";
            const [fg, bg, label] = NIVEL_META[nivel] ?? ["#374151", "#E5E7EB", nivel || "—"];
            const hits = r?.hits ?? [];
            const quienes = Array.from(new Set(hits.map((h) => h.identidad ?? h.ip ?? "?")));
            const resumen = r?.veredicto?.resumen
              ?? (hits[0] ? `${hits[0].titulo}${hits.length > 1 ? ` (+${hits.length - 1})` : ""}` : e.texto);
            const key = `${e.ts}#${e.tipo}`;
            const acc = r?.veredicto?.accion;
            const [afg, abg, alabel] = acc ? (ACCION_META[acc] ?? ["#374151", "#E5E7EB", acc]) : ["", "", ""];
            const tomada = r?.accion_tomada;
            const inv = r?.investigacion ?? {};
            const ipPrincipal = hits.find((h) => h.ip)?.ip ?? null;
            const open = abierta === key;
            return (
              <Fragment key={key}>
                <TableRow hover sx={{ cursor: "pointer" }} onClick={() => setAbierta(open ? null : key)}>
                  <TableCell padding="checkbox">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtArDateTime(e.ts)}</TableCell>
                  <TableCell><Chip label={label} size="small" sx={{ fontWeight: 700, fontSize: 11, height: 22, color: fg, bgcolor: bg }} /></TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {Array.from(new Set(hits.map((h) => h.detector))).map((d) => (
                      <Tooltip key={d} title={hits.find((h) => h.detector === d)?.titulo ?? d}>
                        <Chip label={d} size="small" variant="outlined" sx={{ mr: 0.5, fontSize: 11, height: 20 }} />
                      </Tooltip>
                    ))}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {quienes.slice(0, 3).join(", ")}{quienes.length > 3 ? ` +${quienes.length - 3}` : ""}
                    {e.pais ? <> · {countryFlag(e.pais)} {e.pais}</> : null}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }} title={r?.veredicto?.por_que ?? e.texto}>
                    {resumen}
                    {!r?.veredicto && nivel && <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}> · sin veredicto de Claude</Typography>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {tomada ? (
                      <Tooltip title={`${tomada.por} · ${fmtArDateTime(tomada.ts)}${tomada.nota ? ` · ${tomada.nota}` : ""}`}>
                        <Chip label={`✓ ${ACCION_LABEL[tomada.accion] ?? tomada.accion}`} size="small"
                          sx={{ fontSize: 11, height: 22, fontWeight: 700, color: "#065F46", bgcolor: "#D1FAE5" }} />
                      </Tooltip>
                    ) : acc ? (
                      <Tooltip title={r?.veredicto?.accion_por_que ?? ""}>
                        <Chip label={alabel} size="small" variant="outlined" sx={{ fontSize: 11, height: 22, color: afg, bgcolor: abg, borderColor: afg }} />
                      </Tooltip>
                    ) : <Typography variant="caption" sx={{ color: "text.secondary" }}>—</Typography>}
                  </TableCell>
                  <TableCell padding="checkbox" onClick={(ev) => ev.stopPropagation()}>
                    <Tooltip title={copiada === key ? "¡Copiado!" : "Copiar prompt (para investigarlo con Claude)"}>
                      <IconButton size="small" onClick={() => copiarPrompt(e, key)}><ContentCopyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={8} sx={{ p: 0, borderBottom: open ? undefined : "none" }}>
                    <Collapse in={open} unmountOnExit>
                      <Box sx={{ px: 2, py: 1.5, bgcolor: "#FAFAFA" }}>
                        {r?.veredicto && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2">{r.veredicto.por_que}</Typography>
                            {acc && <Typography variant="body2" sx={{ mt: 0.5 }}><b>Recomienda {alabel.toLowerCase()}:</b> {r.veredicto.accion_por_que}</Typography>}
                            {r.veredicto.proximos_pasos?.length > 0 && (
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                                Próximos pasos: {r.veredicto.proximos_pasos.join(" · ")}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {Object.values(inv).length > 0 ? (
                          <>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>INVESTIGACIÓN AUTOMÁTICA</Typography>
                            {Object.values(inv).map((i) => <InvestigacionIp key={i.ip} inv={i} />)}
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>Sin investigación automática para este hit.</Typography>
                        )}
                        {hits.length > 0 && (
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1, fontFamily: "monospace" }}>
                            {hits.map((h) => `${h.detector} ${h.ip ?? h.identidad ?? ""} n=${h.n} ${JSON.stringify(h.detalle ?? {}).slice(0, 160)}`).join(" | ")}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", mr: 1 }}>DECIDIR:</Typography>
                          <Button size="small" variant="outlined" disabled={!e.sk || !hits[0]?.clave} onClick={() => pedir(e, "suprimir")}>Suprimir 7 días</Button>
                          <Button size="small" variant="outlined" color="success" disabled={!e.sk || !ipPrincipal} onClick={() => pedir(e, "vendor_conocido")}>Vendor conocido</Button>
                          <Button size="small" variant="outlined" color="inherit" disabled={!e.sk} onClick={() => pedir(e, "ignorar")}>Ignorar</Button>
                          <Button size="small" variant="contained" color="error" startIcon={<BlockIcon />} disabled={!ipPrincipal}
                            component={RouterLink} to={`/monitoreo/ips?ip=${encodeURIComponent(ipPrincipal ?? "")}`}>
                            Bloquear en IPs…
                          </Button>
                          {tomada && <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            Ya decidido: {ACCION_LABEL[tomada.accion] ?? tomada.accion} por {tomada.por} el {fmtArDateTime(tomada.ts)}.
                          </Typography>}
                        </Stack>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      <Dialog open={!!pendiente} onClose={() => !busy && setPendiente(null)} fullWidth maxWidth="sm">
        <DialogTitle>{pendiente ? ACCION_LABEL[pendiente.accion] : ""}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {pendiente?.accion === "suprimir" && <>El hit <b>{pendiente.clave}</b> no vuelve a avisar ni a escribir en la bitácora durante 7 días. El resto de los detectores sigue.</>}
            {pendiente?.accion === "vendor_conocido" && <>La IP <b>{pendiente.ip}</b> queda como oficina/integración de un vendor{pendiente.iid ? <> (iid {pendiente.iid})</> : null}: el detector de volumen (D7) deja de mirarla. Los demás detectores la siguen mirando.</>}
            {pendiente?.accion === "ignorar" && <>Se asienta que lo viste y no hace falta nada. No cambia ningún filtro.</>}
            {" "}Queda en la bitácora con tu usuario.
          </DialogContentText>
          <TextField autoFocus fullWidth size="small" label="Nota (opcional)" value={pendiente?.nota ?? ""}
            onChange={(ev) => setPendiente((p) => p ? { ...p, nota: ev.target.value } : p)} inputProps={{ maxLength: 300 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendiente(null)} disabled={busy}>Cancelar</Button>
          <Button onClick={confirmar} disabled={busy} variant="contained">{busy ? "Aplicando…" : "Confirmar"}</Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
}

function StatTile({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box sx={{ minWidth: 140, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15, color: color ?? "#222" }}>{value}</Typography>
    </Box>
  );
}

function BitacoraTable({ rows, expanded, onToggle }: {
  rows: BitacoraEntry[]; expanded: Set<string>; onToggle: (k: string) => void;
}) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: "nowrap" }}>Fecha</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>País</TableCell>
            <TableCell>Quién</TableCell>
            <TableCell>Detalle</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
              Bitácora vacía — usá Investigar o dejá un comentario.
            </TableCell></TableRow>
          )}
          {rows.map((e) => {
            const key = `${e.ts}#${e.pais ?? ""}#${e.tipo}`;
            const [fg, bg, label] = TIPO_META[e.tipo] ?? ["#374151", "#E5E7EB", e.tipo];
            const open = expanded.has(key);
            const hasDetail = e.tipo === "investigacion" && !!e.resultado?.rows?.length;
            return (
              <Fragment key={key}>
                <TableRow hover>
                  <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtArDateTime(e.ts)}</TableCell>
                  <TableCell><Chip label={label} size="small"
                    sx={{ fontWeight: 700, fontSize: 11, height: 22, color: fg, bgcolor: bg }} /></TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {e.pais ? <>{countryFlag(e.pais)} {e.pais}</> : "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{e.por}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{e.texto}</TableCell>
                  <TableCell align="right">
                    {hasDetail && (
                      <IconButton size="small" onClick={() => onToggle(key)}>
                        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
                {hasDetail && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, border: open ? undefined : "none" }}>
                      <Collapse in={open} unmountOnExit>
                        <Box sx={{ py: 1.5, pl: 2 }}>
                          <Table size="small" sx={{ maxWidth: 1100 }}>
                            <TableHead><TableRow>
                              <TableCell>IP</TableCell><TableCell>Host</TableCell>
                              <TableCell>URI</TableCell><TableCell>Acción WAF</TableCell>
                              <TableCell align="right">Requests</TableCell>
                              <TableCell align="right" title="Código HTTP que devolvimos, según los access logs del ALB">Nuestra respuesta</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                              {e.resultado!.rows.map((r, i) => (
                                <TableRow key={i}>
                                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.ip}</TableCell>
                                  <TableCell sx={{ fontSize: 12 }}>{r.host}</TableCell>
                                  <TableCell sx={{ fontSize: 12, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>{r.uri}</TableCell>
                                  <TableCell sx={{ fontSize: 12, color: r.action === "BLOCK" ? "#B91C1C" : "#166534" }}>{r.action}</TableCell>
                                  <TableCell align="right" sx={{ fontSize: 12 }}>{nf.format(r.requests)}</TableCell>
                                  <TableCell align="right" sx={{ fontSize: 12 }}><Respuestas rows={r.respuestas} /></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          {e.resultado?.respuestas_error && (
                            <Typography variant="caption" sx={{ color: "#B45309", display: "block", mt: 1 }}>
                              Sin "nuestra respuesta": {e.resultado.respuestas_error}
                            </Typography>
                          )}
                          {e.resultado?.detalle_ip && Object.keys(e.resultado.detalle_ip.headers ?? {}).length > 0 && (
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                              IP principal {e.resultado.detalle_ip.ip} — {" "}
                              {Object.entries(e.resultado.detalle_ip.headers)
                                .map(([k, v]) => `${k}: ${v}`).join(" · ")}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function MonitoreoPaises() {
  const [range,   setRange]   = useState<PaisesRange>("day");
  const [data,    setData]    = useState<Paises | null>(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [ok,      setOk]      = useState<string | null>(null);

  // Bitácora
  const [bitacora, setBitacora] = useState<BitacoraEntry[]>([]);
  const [vigia, setVigia]       = useState<BitacoraEntry[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [comment,  setComment]  = useState("");
  const [commentCc, setCommentCc] = useState("");
  const [busyCc,   setBusyCc]   = useState<string | null>(null);   // país con acción en curso
  type GeoAction = "block" | "unblock" | "allow" | "disallow";
  const [confirmGeo, setConfirmGeo] = useState<{ country: string; action: GeoAction } | null>(null);

  async function load(r = range) {
    setLoading(true); setErr(null);
    try { setData(await getPaisesRange(r)); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }
  async function loadBitacora() {
    try {
      const [todo, hits] = await Promise.all([getBitacora(), getBitacora(undefined, "vigia")]);
      // El vigía tiene su propia sección: no se duplica en la bitácora general.
      setBitacora(todo.filter((e) => e.tipo !== "vigia"));
      setVigia(hits.slice(0, 30));
    }
    catch (e: any) { setErr(String(e?.message ?? e)); }
  }
  useEffect(() => { load(range); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [range]);
  useEffect(() => { loadBitacora(); }, []);

  async function onInvestigar(cc: string) {
    setBusyCc(cc); setErr(null); setOk(null);
    try {
      const entry = await investigarPais(cc, range === "week" ? 168 : 24);
      setBitacora((prev) => [entry, ...prev]);
      setExpanded((prev) => new Set(prev).add(`${entry.ts}#${entry.pais ?? ""}#${entry.tipo}`));
      setOk(`Investigación de ${countryName(cc)} guardada en la bitácora.`);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusyCc(null); }
  }

  async function onComment() {
    if (!comment.trim()) return;
    setErr(null); setOk(null);
    try {
      const entry = await addComentario(comment.trim(), commentCc.trim().toUpperCase() || undefined);
      setBitacora((prev) => [entry, ...prev]);
      setComment(""); setCommentCc("");
      setOk("Comentario agregado a la bitácora.");
    } catch (e: any) { setErr(String(e?.message ?? e)); }
  }

  async function onGeo() {
    if (!confirmGeo) return;
    const { country, action } = confirmGeo;
    setBusyCc(country); setErr(null); setOk(null);
    try {
      const r = (action === "allow" || action === "disallow")
        ? await geoAllowToggle(country, action)
        : await geoToggle(country, action);
      setData((prev) => prev ? {
        ...prev, blocked_countries: r.blocked_countries,
        allowed_countries: r.allowed_countries ?? prev.allowed_countries,
      } : prev);
      const msgs: Record<GeoAction, string> = {
        block:    `${countryName(country)} BLOQUEADO a nivel WAF. Sus requests van a aparecer como bloqueadas.`,
        unblock:  `${countryName(country)} desbloqueado.`,
        allow:    `${countryName(country)} HABILITADO en la allowlist geo del WAF: su tráfico ya entra.`,
        disallow: `${countryName(country)} quitado de la allowlist geo: su tráfico se bloquea.`,
      };
      setOk(msgs[action]);
      setConfirmGeo(null);
      await loadBitacora();
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusyCc(null); }
  }

  const blockedSet = new Set(data?.blocked_countries ?? []);
  // Allowlist de infra (regla allow-traffic-only): lo que NO está acá ya se
  // bloquea en el WAF desde siempre; habilitar un país nuevo es tarea de infra.
  const allowSet = new Set(data?.allowed_countries ?? []);
  const hasAllowlist = allowSet.size > 0;
  const rangoLabel = RANGOS.find((r) => r.key === range)?.label ?? "";

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Monitoreo · Países</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Desde qué países se accede a los servicios públicos, según los logs del WAF. Día/semana leen
        los logs en vivo; mes/histórico usan el agregado diario (corre a las 00:30 UTC, cubre hasta ayer).
        El WAF opera con ALLOWLIST de países: lo que no está en ella se bloquea siempre. Desde acá se
        habilita un país nuevo (entra a la allowlist) o se bloquea/desbloquea uno ya habilitado. Todo
        afecta tráfico REAL y queda en la bitácora. Para una IP puntual, usá Monitoreo · IPs.
      </Typography>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" select label="Rango" value={range}
          onChange={(e) => setRange(e.target.value as PaisesRange)} sx={{ minWidth: 180 }}>
          {RANGOS.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
        </TextField>
        {data && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {fmtArDateTime(data.from)} → {fmtArDateTime(data.to)} (hora AR)
            {data.source === "aggregates" ? " · agregado diario" : ""}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refrescar (el backend cachea 5 min)">
          <span><IconButton onClick={() => { load(); loadBitacora(); }} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
      </Stack>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {ok  && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      {data && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <StatTile label={`Requests (${rangoLabel.toLowerCase()})`} value={nf.format(data.total)} />
          <StatTile label="Permitidas" value={nf.format(data.allowed)} color="#166534" />
          <StatTile label="Bloqueadas" value={nf.format(data.blocked)} color={data.blocked ? "#B91C1C" : "#166534"} />
          <StatTile label="Países" value={String(data.countries.length)} />
          <StatTile label="Bloqueados en WAF" value={String(blockedSet.size)}
            color={blockedSet.size ? "#9A3412" : "#6B7280"} />
        </Stack>
      )}

      {hasAllowlist && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "#F0FDF4" }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Tooltip title="Regla allow-traffic-only del WAF: todo país fuera de esta lista se bloquea siempre. La ✕ lo quita de la allowlist (Argentina no se puede quitar).">
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#166534", cursor: "help" }}>
                Allowlist geo ({allowSet.size} países habilitados):
              </Typography>
            </Tooltip>
            {[...allowSet].sort().map((cc) => (
              <Chip key={cc} size="small" variant={blockedSet.has(cc) ? "filled" : "outlined"}
                label={`${countryFlag(cc)} ${cc}${blockedSet.has(cc) ? " · bloqueado" : ""}`}
                onDelete={busyCc || cc === "AR" ? undefined : () => setConfirmGeo({ country: cc, action: "disallow" })}
                sx={{ fontSize: 12, ...(blockedSet.has(cc) ? { color: "#9A3412", bgcolor: "#FFEDD5" } : {}) }} />
            ))}
          </Stack>
        </Paper>
      )}
      {blockedSet.size > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "#FFF7ED" }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#9A3412" }}>
              Bloqueados desde este panel:
            </Typography>
            {[...blockedSet].sort().map((cc) => (
              <Chip key={cc} size="small" label={`${countryFlag(cc)} ${countryName(cc)}`}
                onDelete={busyCc ? undefined : () => setConfirmGeo({ country: cc, action: "unblock" })}
                sx={{ fontSize: 12 }} />
            ))}
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              (la ✕ desbloquea)
            </Typography>
          </Stack>
        </Paper>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>País</TableCell>
              <TableCell align="right">Requests</TableCell>
              <TableCell align="right">% del total</TableCell>
              <TableCell align="right">Bloqueadas</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Servicios (host)</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !data && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {data?.countries.map((c) => {
              const pct = data.total ? (100 * c.total / data.total) : 0;
              const isBlocked = blockedSet.has(c.country);               // regla del panel
              const notAllowed = hasAllowlist && !allowSet.has(c.country); // fuera de la allowlist de infra
              const canGeo = /^[A-Z]{2}$/i.test(c.country);
              return (
                <TableRow key={c.country} hover sx={isBlocked || notAllowed ? { bgcolor: "#FFF7ED" } : undefined}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <span style={{ marginRight: 8, fontSize: 18 }}>{countryFlag(c.country)}</span>
                    <b>{countryName(c.country)}</b>
                    <Typography component="span" variant="caption" sx={{ color: "text.secondary", ml: 0.75 }}>
                      {c.country}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{nf.format(c.total)}</TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>
                    {pct >= 0.01 ? `${pct.toFixed(2)}%` : "<0.01%"}
                  </TableCell>
                  <TableCell align="right">
                    {c.blocked > 0 ? (
                      <Chip size="small" label={nf.format(c.blocked)}
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, color: "#B91C1C", bgcolor: "#FEE2E2" }} />
                    ) : <Typography variant="caption" sx={{ color: "text.secondary" }}>0</Typography>}
                  </TableCell>
                  <TableCell>
                    {isBlocked ? (
                      <Chip size="small" label="BLOQUEADO"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, color: "#9A3412", bgcolor: "#FFEDD5" }} />
                    ) : notAllowed ? (
                      <Tooltip title="Fuera de la allowlist geo (allow-traffic-only): su tráfico se bloquea siempre. Usá Habilitar para sumarlo a la allowlist.">
                        <Chip size="small" label="NO HABILITADO"
                          sx={{ fontWeight: 700, fontSize: 11, height: 22, color: "#B91C1C", bgcolor: "#FEE2E2" }} />
                      </Tooltip>
                    ) : (
                      <Chip size="small" label="HABILITADO"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, color: "#166534", bgcolor: "#DCFCE7" }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {c.hosts.map((h) => (
                        <Chip key={h.host} size="small" variant="outlined"
                          label={`${h.host} · ${nf.format(h.requests)}`} sx={{ fontSize: 11, height: 22 }} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Investigar: top IPs, servicios y URIs de este país → queda en la bitácora">
                      <span>
                        <IconButton size="small" disabled={!!busyCc} onClick={() => onInvestigar(c.country)}>
                          {busyCc === c.country ? <CircularProgress size={16} /> : <TravelExploreIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {canGeo && notAllowed && (
                      <Tooltip title="Habilitar este país: entra a la allowlist geo del WAF y su tráfico empieza a pasar">
                        <span>
                          <IconButton size="small" color="success" disabled={!!busyCc}
                            onClick={() => setConfirmGeo({ country: c.country, action: "allow" })}>
                            <AddCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canGeo && !notAllowed && (isBlocked ? (
                      <Tooltip title="Desbloquear este país en el WAF">
                        <span>
                          <IconButton size="small" color="success" disabled={!!busyCc}
                            onClick={() => setConfirmGeo({ country: c.country, action: "unblock" })}>
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={c.country === "AR" ? "Argentina no se puede bloquear" : "Bloquear este país en el WAF (afecta tráfico real)"}>
                        <span>
                          <IconButton size="small" color="error" disabled={!!busyCc || c.country === "AR"}
                            onClick={() => setConfirmGeo({ country: c.country, action: "block" })}>
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ))}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bitácora */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Bitácora</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <TextField size="small" label="Comentario (texto libre)" value={comment} multiline maxRows={3}
            onChange={(e) => setComment(e.target.value)} sx={{ flex: 1, minWidth: 320 }}
            placeholder="Ej: habilitamos MX para el cliente X; el tráfico de FR es VallecitosWater…" />
          <TextField size="small" label="País (opcional)" value={commentCc}
            onChange={(e) => setCommentCc(e.target.value.toUpperCase().slice(0, 2))}
            sx={{ width: 120 }} inputProps={{ maxLength: 2 }} />
          <Button variant="contained" startIcon={<AddCommentIcon />} onClick={onComment}
            disabled={!comment.trim()}>Agregar</Button>
        </Stack>
      </Paper>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>Vigía</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
        Cada 10 minutos una Lambda revisa los últimos 15 de los logs de producción (WAF, adminapi, API
        pública y access logs del ALB) buscando ataques en curso: token que no es de admin con éxito en
        adminapi (D1), enumeración de tenants (D2), IP sin historia operando adminapi (D3), ráfaga de login
        (D4), sondeo seguido de éxito (D5), onboarding y luego adminapi (D6) y volumen anómalo (D7). Claude
        da el veredicto y, con la investigación automática de cada IP (quién es, usuarios y vendor, qué pega, historia),
        recomienda una acción. Sólo ACTUAR manda aviso. Abrí una fila para ver la investigación y decidir:
        suprimir, marcar vendor conocido o ignorar se aplican acá; el bloqueo en el WAF sigue siendo manual, desde Monitoreo · IPs.
      </Typography>
      <VigiaTable rows={vigia} onCambio={loadBitacora} />

      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>Bitácora</Typography>
      <BitacoraTable rows={bitacora} expanded={expanded}
        onToggle={(k) => setExpanded((prev) => {
          const next = new Set(prev);
          next.has(k) ? next.delete(k) : next.add(k);
          return next;
        })} />

      <Dialog open={!!confirmGeo} onClose={() => !busyCc && setConfirmGeo(null)}>
        <DialogTitle>
          {confirmGeo?.action === "block" ? "Bloquear país en el WAF"
            : confirmGeo?.action === "allow" ? "Habilitar país en la allowlist geo"
            : confirmGeo?.action === "disallow" ? "Quitar país de la allowlist geo"
            : "Desbloquear país"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmGeo?.action === "block" && (
              <>Vas a <b>bloquear TODO el tráfico desde {countryName(confirmGeo.country)} ({confirmGeo.country})</b> hacia
                los servicios públicos (regla en el WAF del ALB). Esto afecta tráfico real en producción —
                si hay un cliente o integración legítima en ese país, deja de funcionar. El cambio queda
                asentado en la bitácora. ¿Continuar?</>
            )}
            {confirmGeo?.action === "unblock" && (
              <>Vas a <b>desbloquear {countryName(confirmGeo.country)} ({confirmGeo.country})</b>:
                su tráfico vuelve a entrar normalmente. Queda asentado en la bitácora. ¿Continuar?</>
            )}
            {confirmGeo?.action === "allow" && (
              <>Vas a <b>habilitar {countryName(confirmGeo.country)} ({confirmGeo.country})</b> en la allowlist
                geo del WAF: TODO el tráfico desde ese país empieza a llegar a los servicios públicos
                (sujeto al resto de las reglas del WAF). Si solo necesitás una IP, es más prudente
                habilitarla desde Monitoreo · IPs. Queda asentado en la bitácora. ¿Continuar?</>
            )}
            {confirmGeo?.action === "disallow" && (
              <>Vas a <b>quitar {countryName(confirmGeo.country)} ({confirmGeo.country})</b> de la allowlist geo:
                todo su tráfico pasa a bloquearse en producción. Si hay un cliente o integración legítima
                en ese país, deja de funcionar. Queda asentado en la bitácora. ¿Continuar?</>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGeo(null)} disabled={!!busyCc}>Cancelar</Button>
          <Button onClick={onGeo} disabled={!!busyCc} variant="contained"
            color={confirmGeo?.action === "block" || confirmGeo?.action === "disallow" ? "error" : "success"}>
            {busyCc ? "Aplicando…"
              : confirmGeo?.action === "block" ? "Bloquear"
              : confirmGeo?.action === "allow" ? "Habilitar"
              : confirmGeo?.action === "disallow" ? "Quitar de la allowlist"
              : "Desbloquear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
